import { ArrowDownToLine, ArrowUpFromLine, MoreHorizontal } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { TransactionTierSetting } from "@/interfaces/transaction-tier-settings";

interface TransactionSettingMobileCardProps {
  setting: TransactionTierSetting;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}

const formatMoney = (value: string | number) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "$0.00";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(amount);
};

const tierLabel = (tier: string) => {
  return tier
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export function TransactionSettingMobileCard({
  setting,
  onEdit,
  onToggle,
  onDelete,
}: TransactionSettingMobileCardProps) {
  const isTransfer = setting.type === "TRANSFER";

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-4">
      {/* Header */}

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-500/10 text-teal-400">
            {isTransfer ? (
              <ArrowUpFromLine className="h-4 w-4" />
            ) : (
              <ArrowDownToLine className="h-4 w-4" />
            )}
          </div>

          <div>
            <p className="text-sm font-semibold text-white">
              {tierLabel(setting.tier)}
            </p>

            <p className="mt-0.5 text-xs text-slate-500">
              {isTransfer ? "Transfer" : "Withdrawal"}
            </p>
          </div>
        </div>

        <Badge
          className={
            setting.enabled
              ? "border border-emerald-500/20 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/10"
              : "border border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-800"
          }
        >
          {setting.enabled ? "Active" : "Disabled"}
        </Badge>
      </div>

      {/* Range */}

      <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
        <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
          Transaction range
        </p>

        <div className="mt-1 flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-slate-200">
            {formatMoney(setting.minAmount)}
          </p>

          <span className="text-xs text-slate-600">to</span>

          <p className="text-right text-sm font-semibold text-slate-200">
            {formatMoney(setting.maxAmount)}
          </p>
        </div>
      </div>

      {/* Fee */}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Metric label="Fee" value={`${setting.feePercent}%`} />

        <Metric label="Fee Cap" value={formatMoney(setting.feeCap)} />
      </div>

      {/* Limits */}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Metric label="Daily" value={formatMoney(setting.dailyLimit)} />

        <Metric label="Weekly" value={formatMoney(setting.weeklyLimit)} />

        <Metric label="Monthly" value={formatMoney(setting.monthlyLimit)} />

        <Metric label="Yearly" value={formatMoney(setting.yearlyLimit)} />
      </div>

      {/* Actions */}

      <div className="mt-4 flex gap-2 border-t border-slate-800 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onEdit}
          className="flex-1 border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          Edit
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={onToggle}
          className="flex-1 border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          {setting.enabled ? "Disable" : "Enable"}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={onDelete}
          className="h-10 w-10 shrink-0 border-red-500/20 bg-red-500/5 px-0 text-red-400 hover:bg-red-500/10 hover:text-red-300"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </p>

      <p className="mt-1 truncate text-sm font-medium text-slate-200">
        {value}
      </p>
    </div>
  );
}
